/**
 * Admin V3 — Contacts — per-row status triage control (HIDE-RBAC)
 *
 * Advances a contact message through its status lifecycle
 * (new → read → replied → archived) via the EXISTING backend endpoint
 * PATCH /contacts/{id}?status=<s>. Because the backend takes `status` as a
 * QUERY parameter (FastAPI simple type), the value is carried in the endpoint
 * URL and the request body is empty — this needs NO backend change.
 *
 * Each target status owns its own mutation hook so the endpoint is deterministic
 * at render time (no effects, fully testable) — mirroring the "each row owns its
 * own mutation hook" pattern of CorporateStatusToggle.
 *
 * RBAC: controls are HIDE-gated behind can('admin:platform:overview') — the same
 * permission the backend @rbac_permission enforces on the PATCH.
 */

import React from 'react';
import { Loader2, Mail, MailOpen, CornerUpLeft, Archive } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const CONTACT_TRIAGE_PERMISSION = 'admin:platform:overview';

const ContactsStatusControl = ({ contact, onTriaged, locale = DEFAULT_LOCALE }) => {
  const { can } = usePermissions();
  const { showSuccess, showError } = useToast();

  const id = contact?.id || '_';
  const current = contact?.status || 'new';

  // One hook per target status → deterministic endpoint (query-param carried).
  const mRead     = useAdminMutation(`/contacts/${id}?status=read`, 'patch');
  const mReplied  = useAdminMutation(`/contacts/${id}?status=replied`, 'patch');
  const mArchived = useAdminMutation(`/contacts/${id}?status=archived`, 'patch');

  const anyLoading = mRead.loading || mReplied.loading || mArchived.loading;

  // Backend requires the RBAC permission on PATCH; hide the whole control when
  // it cannot be confirmed (fail-closed HIDE).
  if (!can(CONTACT_TRIAGE_PERMISSION)) return null;

  const applyStatus = (m, targetStatus) => {
    m.mutate(
      {},
      {
        onSuccess: () => {
          showSuccess(t('contacts.triage.success', locale));
          if (onTriaged) onTriaged();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('contacts.triage.error', locale)),
      },
    );
  };

  const ACTIONS = [
    { key: 'read',     m: mRead,     icon: MailOpen,     labelKey: 'contacts.action.markRead' },
    { key: 'replied',  m: mReplied,  icon: CornerUpLeft, labelKey: 'contacts.action.markReplied' },
    { key: 'archived', m: mArchived, icon: Archive,      labelKey: 'contacts.action.archive' },
  ];

  return (
    <div className="flex items-center gap-1" data-testid={`adminv3-contacts-triage-${id}`}>
      {anyLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
      {ACTIONS.map(({ key, m, icon: Icon, labelKey }) => (
        <Button
          key={key}
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[12px] text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          disabled={anyLoading || current === key}
          onClick={() => applyStatus(m, key)}
          title={t(labelKey, locale)}
          data-testid={`adminv3-contacts-status-${key}-${id}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </Button>
      ))}
    </div>
  );
};

export { CONTACT_TRIAGE_PERMISSION };
export default ContactsStatusControl;
